import { error as debugError } from '@/ts/debug';

export async function openDirectory() {
  if (! window.mediaDirHandle) {
    window.opfsRoot = await navigator.storage.getDirectory();
    window.mediaDirHandle = await window.opfsRoot.getDirectoryHandle("media", {create: true});
  }
}

export async function getFileUrl(name: string) {
  try {
    const fileHandle = await window.mediaDirHandle.getFileHandle(name, {create: false});
    const file = await fileHandle.getFile();
    if (file.size > 0) {
      const file = await fileHandle.getFile();
      const src = URL.createObjectURL(file);
      return src;
    } else {
      return null;
    }
  } catch {
    return null;
  }
}

export async function checkFileInCache(name: string) {
  try {
    const fileHandle = await window.mediaDirHandle.getFileHandle(name, {create: false});
    const file = await fileHandle.getFile();
    if (file.size > 0) {
      return true;
    } else {
      return false;
    }
  } catch {
    return false;
  }
}

export async function checkOrCachePhoto(url: string) {
  const filename = url.replaceAll("/", "_").replaceAll(":", "_");
  const exists = await checkFileInCache(filename);
  if (! exists) {
    const thumb = await downloadThumb(url);
    if (thumb) {
      const fileHandle = await window.mediaDirHandle.getFileHandle(filename, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(thumb);
      await writable.close();
    }
  }
}

export async function downloadThumb(url: string) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    } else {
      const blob = await response.blob();
      return blob;
    }
  } catch (error) {
    return null;
  }
}

export async function openGridDirectory() {
  if (!window.gridDirHandle) {
    if (!window.opfsRoot) {
      window.opfsRoot = await navigator.storage.getDirectory();
    }
    window.gridDirHandle = await window.opfsRoot.getDirectoryHandle("grids", {create: true});
  }
}

export async function saveGridToCache(fileName: string, gridData: any) {
  try {
    const fileHandle = await window.gridDirHandle.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    
    // Get dimensions
    const rows = gridData.length;
    const cols = rows > 0 ? gridData[0].length : 0;
    
    // Create buffer for binary data
    // 8 bytes for rows + 8 bytes for cols + (rows * cols * 4 * 8) bytes for tile coordinates
    const totalElements = rows * cols * 4 * 2; // 4 points per tile, 2 floats per point
    const buffer = new ArrayBuffer(16 + totalElements * 8);
    const view = new DataView(buffer);
    
    // Write rows and cols as BigInt64
    view.setBigInt64(0, BigInt(rows), true);
    view.setBigInt64(8, BigInt(cols), true);
    
    // Write tile data as Float64
    let offset = 16;
    for (let i = 0; i < rows; i++) {
      const row = gridData[i];
      if (!row) continue;
      for (let j = 0; j < cols; j++) {
        const tile = row[j];
        if (!tile) continue;
        // Each tile has 4 points, each point has [x, y]
        for (let p = 0; p < 4; p++) {
          view.setFloat64(offset, tile[p][0], true); // x
          offset += 8;
          view.setFloat64(offset, tile[p][1], true); // y
          offset += 8;
        }
      }
    }
    
    await writable.write(buffer);
    await writable.close();
  } catch (error) {
    debugError('Error saving grid to cache:', error);
  }
}

export async function loadGridFromCache(fileName: string) {
  try {
    const fileHandle = await window.gridDirHandle.getFileHandle(fileName, { create: false });
    const file = await fileHandle.getFile();
    if (file.size < 16) {
      return null; // File too small to contain header
    }
    
    const arrayBuffer = await file.arrayBuffer();
    const view = new DataView(arrayBuffer);
    
    // Read rows and cols from first 16 bytes
    const rows = Number(view.getBigInt64(0, true));
    const cols = Number(view.getBigInt64(8, true));
    
    if (rows <= 0 || cols <= 0) {
      return null;
    }
    
    // Reconstruct 2D grid
    const gridData = [];
    let offset = 16;
    
    for (let i = 0; i < rows; i++) {
      const row = [];
      for (let j = 0; j < cols; j++) {
        const tile = [];
        // Read 4 points, each with [x, y]
        for (let p = 0; p < 4; p++) {
          const x = view.getFloat64(offset, true);
          offset += 8;
          const y = view.getFloat64(offset, true);
          offset += 8;
          tile.push([x, y]);
        }
        row.push(tile);
      }
      gridData.push(row);
    }
    
    return gridData;
  } catch {
    return null;
  }
}

export async function checkGridInCache(fileName: string): Promise<boolean> {
  try {
    await window.gridDirHandle.getFileHandle(fileName, { create: false });
    return true;
  } catch {
    return false;
  }
}